import { fetchDetailedPricing as fetchDetailedPricingApi } from './api';
import { createAppSlice } from './createAppSlice';
import { parseApiError } from './errors';

import type { DetailedPricing, DetailedPricingError } from './api';

type DetailedPricingErrorCode = DetailedPricingError['errorCode'] | 'UNKNOWN_ERROR';

// Compile-time exhaustiveness: Record<K, true> requires ALL union members as keys.
const PRICING_ERROR_CODE_MAP: Record<DetailedPricingError['errorCode'], true> = {
    SESSION_INVALID: true,
    UNKNOWN: true,
};
const PRICING_ERROR_CODES = Object.keys(PRICING_ERROR_CODE_MAP) as readonly DetailedPricingError['errorCode'][];

const KNOWN_PRICING_ERROR_CODES: ReadonlySet<DetailedPricingError['errorCode']> = new Set(PRICING_ERROR_CODES);

interface DetailedPricingRejectedPayload {
    errorCode: DetailedPricingErrorCode;
    message: string;
    unrecognizedCode?: string;
}

/** Discriminated union on `status`. Each variant defines exactly which fields
 *  are present, making illegal states (e.g., data + errorCode) unrepresentable.
 *  `requestedOrderId` is a staleness guard: if a fulfilled action's
 *  payload.orderId or a rejected action's meta.arg doesn't match, the response
 *  arrived out-of-order and is discarded. */
type DetailedPricingState =
    | { data: DetailedPricing; errorCode: undefined; requestedOrderId: string; status: 'succeeded' }
    | { data: undefined; errorCode: DetailedPricingErrorCode; requestedOrderId: string; status: 'failed' }
    | { data: undefined; errorCode: undefined; requestedOrderId: string; status: 'pending' }
    | { data: undefined; errorCode: undefined; requestedOrderId: undefined; status: 'idle' };

const initialState = {
    data: undefined,
    errorCode: undefined,
    requestedOrderId: undefined,
    status: 'idle',
} satisfies DetailedPricingState as DetailedPricingState;

const detailedPricingSlice = createAppSlice({
    initialState,
    name: 'detailedPricing',
    reducers: (create) => ({
        detailedPricingCleared: create.reducer(() => initialState),
        fetchDetailedPricing: create.asyncThunk(
            async (orderId: string, { rejectWithValue }) => {
                try {
                    return await fetchDetailedPricingApi(orderId);
                } catch (error: unknown) {
                    return rejectWithValue(parseApiError(error, {
                        expectedCategories: ['CLIENT_ERROR', 'SYSTEM_ERROR'] satisfies DetailedPricingError['errorCategory'][],
                        knownCodes: KNOWN_PRICING_ERROR_CODES,
                    }));
                }
            },
            {
                fulfilled: (state, action) => {
                    // Staleness guard: discard if a newer request has been made
                    if (state.requestedOrderId
                        && action.payload.orderId !== state.requestedOrderId) {
                        return;
                    }
                    // eslint-disable-next-line consistent-return -- void = keep state (stale guard), object = replace state
                    return {
                        data: action.payload,
                        errorCode: undefined,
                        requestedOrderId: state.requestedOrderId ?? action.payload.orderId,
                        status: 'succeeded',
                    } satisfies DetailedPricingState;
                },
                pending: (_state, action) => ({
                    data: undefined,
                    errorCode: undefined,
                    requestedOrderId: action.meta.arg,
                    status: 'pending',
                }) satisfies DetailedPricingState,
                rejected: (state, action) => {
                    // Staleness guard: discard if a newer request has been made
                    if (state.requestedOrderId && action.meta.arg !== state.requestedOrderId) {
                        return;
                    }
                    const payload = action.payload as
                        DetailedPricingRejectedPayload | undefined;
                    // eslint-disable-next-line consistent-return -- void = keep state (stale guard), object = replace state
                    return {
                        data: undefined,
                        errorCode: payload?.errorCode ?? 'UNKNOWN_ERROR',
                        requestedOrderId: state.requestedOrderId ?? action.meta.arg,
                        status: 'failed',
                    } satisfies DetailedPricingState;
                },
            },
        ),
    }),
    selectors: {
        selectDetailedPricing: (state) => state.data,
        selectDetailedPricingErrorCode: (state) => state.errorCode,
        selectDetailedPricingStatus: (state) => state.status,
    },
});

export type { DetailedPricingErrorCode, DetailedPricingRejectedPayload, DetailedPricingState };
export { PRICING_ERROR_CODES };
export const { detailedPricingCleared, fetchDetailedPricing } = detailedPricingSlice.actions;
export const detailedPricingReducer = detailedPricingSlice.reducer;
export const getDetailedPricingSelectors = detailedPricingSlice.getSelectors;
export const {
    selectDetailedPricing,
    selectDetailedPricingErrorCode,
    selectDetailedPricingStatus,
} = detailedPricingSlice.selectors;
